const WEBGL_HOVER_SELECTOR = '[data-webgl-hover]';
const WEBGL_HOVER_IGNORE_SELECTOR = '[data-webgl-hover-ignore]';
const WEBGL_HOVER_SCOPE_SELECTOR = '[data-webgl-hover-scope]';
const WEBGL_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const WEBGL_HOVER_DESKTOP_BREAKPOINT = 1033;
const DEFAULT_VARIANT = 'wave';

const VARIANT_MAP = {
  wave: 0,
  liquid: 1,
  glitch: 2,
  zoom: 3,
  noise: 4,
  shine: 5,
  prism: 6,
  drift: 7,
  swirl: 8,
  wobble: 9,
  ripple: 1,
  twist: 8,
};

const webglHoverInstances = new Map();
const reducedMotionMedia = window.matchMedia(WEBGL_REDUCED_MOTION_QUERY);

let resizeFrameId = null;
let renderLoopFrameId = null;

const VERTEX_SHADER_SOURCE = `
  attribute vec2 aPosition;
  varying vec2 vUv;

  void main() {
    vUv = aPosition * 0.5 + 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER_SOURCE = `
  precision highp float;

  uniform sampler2D uTexture;
  uniform float uTime;
  uniform float uHover;
  uniform float uVariant;
  uniform float uStrength;
  uniform vec2 uPlane;
  uniform vec2 uImage;

  varying vec2 vUv;

  vec2 coverUv(vec2 uv, vec2 plane, vec2 image) {
    float planeRatio = plane.x / max(plane.y, 1.0);
    float imageRatio = image.x / max(image.y, 1.0);
    vec2 result = uv;

    if (planeRatio < imageRatio) {
      float scale = planeRatio / imageRatio;
      result.y = uv.y * scale + (1.0 - scale) * 0.5;
    } else {
      float scale = imageRatio / planeRatio;
      result.x = uv.x * scale + (1.0 - scale) * 0.5;
    }

    return result;
  }

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);

    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  vec2 distortWave(vec2 uv, float hover, float time, float strength) {
    vec2 offset = vec2(0.0);
    offset.x += sin(uv.y * 13.0 + time * 2.1) * (0.0015 + 0.0060 * hover);
    offset.y += cos(uv.x * 11.0 + time * 1.8) * 0.0012 * hover;
    return offset * strength;
  }

  vec2 distortLiquid(vec2 uv, float hover, float time, float strength) {
    vec2 centered = uv - vec2(0.5);
    float dist = length(centered);
    vec2 direction = normalize(centered + vec2(0.0001));
    vec2 offset = vec2(0.0);

    offset.x += sin(uv.y * 18.0 + time * 1.7) * 0.0045;
    offset.x += cos(uv.x * 7.5 - time * 1.1) * 0.0028;
    offset.y += cos(uv.x * 15.0 - time * 1.5) * 0.0040;
    offset.y += sin(uv.y * 8.0 + time * 1.2) * 0.0025;
    offset += direction * sin(dist * 26.0 - time * 2.6) * 0.0030;

    offset *= hover * strength * smoothstep(0.92, 0.08, dist);
    return offset;
  }

  vec2 distortSwirl(vec2 uv, float hover, float time, float strength) {
    vec2 centered = uv - vec2(0.5);
    float dist = length(centered);
    float angle = (0.12 + sin(time * 1.6) * 0.04) * hover * strength * smoothstep(0.78, 0.0, dist);
    float s = sin(angle);
    float c = cos(angle);
    mat2 rotation = mat2(c, -s, s, c);
    vec2 rotated = rotation * centered;

    return (rotated - centered) + normalize(centered + vec2(0.0001)) * sin(dist * 28.0 - time * 2.4) * 0.0015 * hover;
  }

  vec2 distortWobble(vec2 uv, float hover, float time, float strength) {
    vec2 offset = vec2(0.0);
    offset.x += sin(uv.y * 24.0 + time * 2.6) * 0.0045;
    offset.y += cos(uv.x * 19.0 - time * 2.1) * 0.0035;
    offset += vec2(1.0, -0.65) * sin((uv.x + uv.y) * 12.0 - time * 2.8) * 0.0025;
    return offset * hover * strength;
  }

  vec2 zoomUv(vec2 uv, float hover, float time, float strength) {
    float scale = 1.0 - (0.05 + sin(time * 1.5) * 0.008) * hover * strength;
    vec2 drift = vec2(sin(time * 0.9), cos(time * 0.75)) * 0.0024 * hover * strength;
    return (uv - 0.5) * scale + 0.5 + drift;
  }

  vec2 driftUv(vec2 uv, float hover, float time, float strength) {
    vec2 shift = vec2(sin(time * 1.55), cos(time * 1.30)) * 0.0135 * hover * strength;
    float pulse = sin(time * 1.85) * 0.0075;
    float scale = 1.0 - (0.052 + pulse) * hover * strength;
    return (uv - 0.5) * scale + 0.5 + shift;
  }

  vec4 zoomSample(vec2 uv, float hover, float time, float strength) {
    vec2 sampleUv = zoomUv(uv, hover, time, strength);
    vec4 color = texture2D(uTexture, sampleUv);
    float focus = smoothstep(0.90, 0.14, distance(vUv, vec2(0.5)));
    color.rgb += focus * 0.06 * hover * strength;
    color.rgb *= 1.0 + hover * 0.08 * strength;
    return color;
  }

  vec4 glitchSample(vec2 uv, float hover, float time, float strength) {
    float band = floor(vUv.y * 28.0);
    float jitter = (hash(vec2(band, floor(time * 18.0))) - 0.5) * 0.020 * hover * strength;
    float chroma = 0.0045 * hover * strength;
    vec2 shift = vec2(jitter, 0.0);

    vec4 color;
    color.r = texture2D(uTexture, uv + shift + vec2(chroma, 0.0)).r;
    color.g = texture2D(uTexture, uv + shift).g;
    color.b = texture2D(uTexture, uv + shift - vec2(chroma, 0.0)).b;
    color.a = texture2D(uTexture, uv + shift).a;

    float scan = sin(vUv.y * 220.0 - time * 18.0) * 0.5 + 0.5;
    color.rgb += (scan - 0.5) * 0.05 * hover * strength;

    return color;
  }

  vec4 noiseSample(vec2 uv, float hover, float time, float strength) {
    vec4 color = texture2D(uTexture, uv);
    vec2 grainUv = vec2(
      floor(vUv.x * uPlane.x * 0.70 + time * 11.0),
      floor(vUv.y * uPlane.y * 0.70 - time * 13.0)
    );
    float grain = hash(grainUv) - 0.5;
    float softNoise = noise2(vUv * 12.0 + vec2(time * 0.35, time * 0.18)) - 0.5;

    color.rgb += grain * 0.055 * hover * strength;
    color.rgb += softNoise * 0.025 * hover * strength;
    color.rgb += vec3(0.02, 0.015, 0.01) * hover * strength * smoothstep(0.88, 0.18, distance(vUv, vec2(0.5)));

    return color;
  }

  vec4 shineSample(vec2 uv, float hover, float time, float strength) {
    vec4 color = texture2D(uTexture, uv);
    float sweepPos = mix(-0.35, 1.35, fract(time * 0.18));
    float band = smoothstep(0.18, 0.0, abs((vUv.x + vUv.y * 0.35) - sweepPos));
    color.rgb += band * 0.18 * hover * strength;
    return color;
  }

  vec4 prismSample(vec2 uv, float hover, float time, float strength) {
    vec2 shift = vec2(sin(time * 1.2), cos(time * 1.5)) * 0.0018 * hover * strength;
    float chroma = 0.0035 * hover * strength;
    vec4 color;

    color.r = texture2D(uTexture, uv + shift + vec2(chroma, 0.0)).r;
    color.g = texture2D(uTexture, uv + shift).g;
    color.b = texture2D(uTexture, uv + shift - vec2(chroma * 0.8, 0.0)).b;
    color.a = texture2D(uTexture, uv + shift).a;

    color.rgb += vec3(0.02, 0.035, 0.05) * hover * strength * smoothstep(0.84, 0.10, distance(vUv, vec2(0.5)));

    return color;
  }

  vec4 driftSample(vec2 uv, float hover, float time, float strength) {
    vec2 sampleUv = driftUv(uv, hover, time, strength);
    vec4 color = texture2D(uTexture, sampleUv);
    color.rgb += vec3(0.015, 0.02, 0.025) * hover * strength;
    return color;
  }

  void main() {
    vec2 uv = coverUv(vUv, uPlane, uImage);
    vec2 sampleUv = uv;
    vec4 color;

    if (uVariant < 0.5) {
      float breathing = sin(uTime * 0.75) * 0.008;
      sampleUv = (uv - 0.5) * (1.0 - breathing - uHover * 0.02) + 0.5;
      sampleUv += distortWave(sampleUv, uHover, uTime, uStrength);
      color = texture2D(uTexture, sampleUv);
    } else if (uVariant < 1.5) {
      float breathing = sin(uTime * 0.7) * 0.010;
      sampleUv = (uv - 0.5) * (1.0 - breathing - uHover * 0.03) + 0.5;
      sampleUv += distortLiquid(sampleUv, uHover, uTime, uStrength);
      color = texture2D(uTexture, sampleUv);
    } else if (uVariant < 2.5) {
      color = glitchSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 3.5) {
      color = zoomSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 4.5) {
      color = noiseSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 5.5) {
      color = shineSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 6.5) {
      color = prismSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 7.5) {
      color = driftSample(sampleUv, uHover, uTime, uStrength);
    } else if (uVariant < 8.5) {
      sampleUv += distortSwirl(sampleUv, uHover, uTime, uStrength);
      color = texture2D(uTexture, sampleUv);
    } else {
      sampleUv += distortWobble(sampleUv, uHover, uTime, uStrength);
      color = texture2D(uTexture, sampleUv);
    }

    float glow = smoothstep(0.95, 0.28, distance(vUv, vec2(0.5)));
    color.rgb += vec3(1.0, 0.63, 0.48) * (0.025 * glow * uHover * uStrength);

    gl_FragColor = color;
  }
`;

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const isDesktopViewport = () => window.innerWidth > WEBGL_HOVER_DESKTOP_BREAKPOINT;

const isWebglHoverEnabled = () => (
  Boolean(window.WebGLRenderingContext)
  && !reducedMotionMedia.matches
  && isDesktopViewport()
);

const resolveVariant = (value) => {
  const normalized = String(value || DEFAULT_VARIANT).trim().toLowerCase();
  return Number.isFinite(VARIANT_MAP[normalized]) ? VARIANT_MAP[normalized] : VARIANT_MAP[DEFAULT_VARIANT];
};

const resolveStrength = (value) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return 1;
  return clampNumber(parsed, 0.35, 1.75);
};

const createShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
};

const createProgram = (gl) => {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE);

  if (!vertexShader || !fragmentShader) {
    if (vertexShader) gl.deleteShader(vertexShader);
    if (fragmentShader) gl.deleteShader(fragmentShader);
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
};
const resolveWebglHoverIgnoreElements = (hostEl) => {
  const ignoreElements = new Set();

  hostEl.querySelectorAll(WEBGL_HOVER_IGNORE_SELECTOR).forEach((ignoreEl) => {
    ignoreElements.add(ignoreEl);
  });

  const scopedContainer = hostEl.closest(WEBGL_HOVER_SCOPE_SELECTOR);

  if (scopedContainer) {
    const scopedHosts = Array.from(scopedContainer.querySelectorAll(WEBGL_HOVER_SELECTOR));

    if (scopedHosts.length === 1 && scopedHosts[0] === hostEl) {
      scopedContainer.querySelectorAll(WEBGL_HOVER_IGNORE_SELECTOR).forEach((ignoreEl) => {
        ignoreElements.add(ignoreEl);
      });

      ignoreElements.delete(hostEl);
      return Array.from(ignoreElements);
    }
  }

  let scopeEl = hostEl.parentElement;

  while (scopeEl) {
    const scopedHosts = Array.from(scopeEl.querySelectorAll(WEBGL_HOVER_SELECTOR));

    if (scopedHosts.length === 1 && scopedHosts[0] === hostEl) {
      scopeEl.querySelectorAll(WEBGL_HOVER_IGNORE_SELECTOR).forEach((ignoreEl) => {
        ignoreElements.add(ignoreEl);
      });
      break;
    }

    scopeEl = scopeEl.parentElement;
  }

  ignoreElements.delete(hostEl);

  return Array.from(ignoreElements);
};


const canRenderWebglHoverInstance = (instance) => (
  !instance.destroyed
  && instance.textureReady
  && instance.isInViewport
  && !document.hidden
  && isDesktopViewport()
);

const shouldAnimateWebglHoverInstance = (instance) => (
  canRenderWebglHoverInstance(instance)
  && (instance.hoverTarget > 0 || instance.hoverState > 0.001)
);

const renderWebglHoverInstance = (instance, time) => {
  const {
    gl,
    canvasEl,
    program,
    texture,
    timeLocation,
    hoverLocation,
    variantLocation,
    strengthLocation,
    planeLocation,
    imageLocation,
    variant,
    strength,
    planeWidth,
    planeHeight,
    imageWidth,
    imageHeight,
  } = instance;

  gl.viewport(0, 0, canvasEl.width, canvasEl.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  gl.useProgram(program);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);

  gl.uniform1f(timeLocation, time);
  gl.uniform1f(hoverLocation, instance.hoverState);
  gl.uniform1f(variantLocation, variant);
  gl.uniform1f(strengthLocation, strength);
  gl.uniform2f(planeLocation, planeWidth, planeHeight);
  gl.uniform2f(imageLocation, imageWidth, imageHeight);

  gl.drawArrays(gl.TRIANGLES, 0, 6);
};

const hasRenderableWebglHoverInstances = () => {
  let hasRenderableInstance = false;

  webglHoverInstances.forEach((instance) => {
    if (hasRenderableInstance || !canRenderWebglHoverInstance(instance)) return;
    if (!instance.needsRender && !shouldAnimateWebglHoverInstance(instance)) return;
    hasRenderableInstance = true;
  });

  return hasRenderableInstance;
};

const stopWebglHoverRenderLoop = () => {
  if (renderLoopFrameId === null) return;

  window.cancelAnimationFrame(renderLoopFrameId);
  renderLoopFrameId = null;
};

const renderWebglHoverEffects = (timestamp) => {
  if (!isWebglHoverEnabled() || document.hidden) {
    renderLoopFrameId = null;
    return;
  }

  let hasActiveAnimation = false;
  const time = timestamp * 0.001;

  webglHoverInstances.forEach((instance) => {
    if (!canRenderWebglHoverInstance(instance)) return;

    if (shouldAnimateWebglHoverInstance(instance)) {
      instance.hoverState += (instance.hoverTarget - instance.hoverState) * 0.08;

      if (Math.abs(instance.hoverTarget - instance.hoverState) < 0.001) {
        instance.hoverState = instance.hoverTarget;
      }

      if (instance.hoverTarget === 0 && instance.hoverState <= 0.001) {
        instance.hoverState = 0;
        renderWebglHoverInstance(instance, 0);
      } else {
        renderWebglHoverInstance(instance, time);
        hasActiveAnimation = true;
      }

      instance.needsRender = false;
      return;
    }

    if (!instance.needsRender) return;

    instance.hoverState = 0;
    renderWebglHoverInstance(instance, 0);
    instance.needsRender = false;
  });

  if (!hasActiveAnimation && !hasRenderableWebglHoverInstances()) {
    renderLoopFrameId = null;
    return;
  }

  renderLoopFrameId = window.requestAnimationFrame(renderWebglHoverEffects);
};

const ensureWebglHoverRenderLoop = () => {
  if (renderLoopFrameId !== null || !isWebglHoverEnabled() || document.hidden) return;
  if (!hasRenderableWebglHoverInstances()) return;

  renderLoopFrameId = window.requestAnimationFrame(renderWebglHoverEffects);
};
const destroyWebglHover = (hostEl) => {
  const instance = webglHoverInstances.get(hostEl);
  if (!instance) return;
  instance.destroy();
};

const destroyAllWebglHoverEffects = () => {
  Array.from(webglHoverInstances.keys()).forEach((hostEl) => destroyWebglHover(hostEl));
  stopWebglHoverRenderLoop();
};

const initWebglHover = (hostEl) => {
  if (webglHoverInstances.has(hostEl)) return false;

  const targetSelector = hostEl.dataset.webglHoverTarget;
  const imageEl = targetSelector
    ? hostEl.querySelector(targetSelector)
    : hostEl.querySelector('img');

  if (!imageEl) return false;

  const imageSource = imageEl.currentSrc || imageEl.src;
  if (!imageSource) return false;

  const originalInlinePosition = hostEl.style.position;
  const hostStyle = window.getComputedStyle(hostEl);
  const positionInjected = hostStyle.position === 'static';

  if (positionInjected) {
    hostEl.style.position = 'relative';
  }

  hostEl.classList.add('webgl-hover-host');
  imageEl.classList.add('webgl-hover-image');

  const canvasEl = document.createElement('canvas');
  canvasEl.className = 'webgl-hover-canvas';

  const imageStyles = window.getComputedStyle(imageEl);
  if (imageStyles.transform && imageStyles.transform !== 'none') {
    canvasEl.style.transform = imageStyles.transform;
  }

  if (imageStyles.borderRadius && imageStyles.borderRadius !== '0px') {
    canvasEl.style.borderRadius = imageStyles.borderRadius;
  }

  hostEl.appendChild(canvasEl);

  const resetHostState = () => {
    canvasEl.remove();
    imageEl.classList.remove('webgl-hover-image');
    hostEl.classList.remove('webgl-hover-host', 'is-webgl-hover-ready');

    if (positionInjected) {
      hostEl.style.position = originalInlinePosition;
    }
  };

  const gl = canvasEl.getContext('webgl', {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
    powerPreference: 'low-power',
  });

  if (!gl) {
    resetHostState();
    return false;
  }

  const program = createProgram(gl);
  if (!program) {
    resetHostState();
    return false;
  }

  const positionLocation = gl.getAttribLocation(program, 'aPosition');
  const textureLocation = gl.getUniformLocation(program, 'uTexture');
  const timeLocation = gl.getUniformLocation(program, 'uTime');
  const hoverLocation = gl.getUniformLocation(program, 'uHover');
  const variantLocation = gl.getUniformLocation(program, 'uVariant');
  const strengthLocation = gl.getUniformLocation(program, 'uStrength');
  const planeLocation = gl.getUniformLocation(program, 'uPlane');
  const imageLocation = gl.getUniformLocation(program, 'uImage');

  if (
    positionLocation < 0
    || textureLocation === null
    || timeLocation === null
    || hoverLocation === null
    || variantLocation === null
    || strengthLocation === null
    || planeLocation === null
    || imageLocation === null
  ) {
    gl.deleteProgram(program);
    resetHostState();
    return false;
  }

  const vertices = new Float32Array([
    -1, -1,
    1, -1,
    -1, 1,
    -1, 1,
    1, -1,
    1, 1,
  ]);

  const vertexBuffer = gl.createBuffer();
  const texture = gl.createTexture();

  if (!vertexBuffer || !texture) {
    if (vertexBuffer) gl.deleteBuffer(vertexBuffer);
    if (texture) gl.deleteTexture(texture);
    gl.deleteProgram(program);
    resetHostState();
    return false;
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    1,
    1,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    new Uint8Array([0, 0, 0, 0]),
  );

  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
  gl.uniform1i(textureLocation, 0);

  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const instance = {
    hostEl,
    imageEl,
    canvasEl,
    gl,
    program,
    vertexBuffer,
    texture,
    timeLocation,
    hoverLocation,
    variantLocation,
    strengthLocation,
    planeLocation,
    imageLocation,
    variant: resolveVariant(hostEl.dataset.webglHover),
    strength: resolveStrength(hostEl.dataset.webglHoverStrength),
    planeWidth: 1,
    planeHeight: 1,
    hoverTarget: 0,
    hoverState: 0,
    hoverSources: 0,
    isInViewport: false,
    textureReady: false,
    imageWidth: 1,
    imageHeight: 1,
    needsRender: false,
    destroyed: false,
    destroy: null,
  };

  const setSize = () => {
    const width = Math.max(hostEl.clientWidth, 1);
    const height = Math.max(hostEl.clientHeight, 1);
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    instance.planeWidth = width;
    instance.planeHeight = height;

    canvasEl.width = Math.round(width * dpr);
    canvasEl.height = Math.round(height * dpr);
    canvasEl.style.width = `${width}px`;
    canvasEl.style.height = `${height}px`;
    instance.needsRender = true;

    if (instance.textureReady) {
      ensureWebglHoverRenderLoop();
    }
  };

  const syncHoverTarget = () => {
    instance.hoverTarget = instance.hoverSources > 0 ? 1 : 0;
  };

  const handlePointerEnter = () => {
    instance.hoverSources += 1;
    syncHoverTarget();
    ensureWebglHoverRenderLoop();
  };

  const handlePointerLeave = () => {
    instance.hoverSources = Math.max(0, instance.hoverSources - 1);
    syncHoverTarget();
    ensureWebglHoverRenderLoop();
  };

  const hoverIgnoreElements = resolveWebglHoverIgnoreElements(hostEl);

  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      instance.isInViewport = entry.isIntersecting;

      if (instance.isInViewport) {
        ensureWebglHoverRenderLoop();
      } else if (!hasRenderableWebglHoverInstances()) {
        stopWebglHoverRenderLoop();
      }
    });
  }, {
    threshold: 0.1,
  });

  const resizeObserver = window.ResizeObserver
    ? new ResizeObserver(() => setSize())
    : null;

  const destroy = () => {
    if (instance.destroyed) return;
    instance.destroyed = true;

    visibilityObserver.disconnect();

    if (resizeObserver) {
      resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', setSize);
    }

    hostEl.removeEventListener('pointerenter', handlePointerEnter);
    hostEl.removeEventListener('pointerleave', handlePointerLeave);

    hoverIgnoreElements.forEach((ignoreEl) => {
      ignoreEl.removeEventListener('pointerenter', handlePointerEnter);
      ignoreEl.removeEventListener('pointerleave', handlePointerLeave);
    });

    webglHoverInstances.delete(hostEl);
    hostEl.removeAttribute('data-webgl-hover-init');

    canvasEl.remove();
    imageEl.classList.remove('webgl-hover-image');
    hostEl.classList.remove('webgl-hover-host', 'is-webgl-hover-ready');

    gl.deleteBuffer(vertexBuffer);
    gl.deleteTexture(texture);
    gl.deleteProgram(program);

    if (positionInjected) {
      hostEl.style.position = originalInlinePosition;
    }

    if (!hasRenderableWebglHoverInstances()) {
      stopWebglHoverRenderLoop();
    }
  };

  instance.destroy = destroy;

  webglHoverInstances.set(hostEl, instance);
  hostEl.dataset.webglHoverInit = '1';

  setSize();

  if (resizeObserver) {
    resizeObserver.observe(hostEl);
  } else {
    window.addEventListener('resize', setSize);
  }

  hostEl.addEventListener('pointerenter', handlePointerEnter);
  hostEl.addEventListener('pointerleave', handlePointerLeave);

  hoverIgnoreElements.forEach((ignoreEl) => {
    ignoreEl.addEventListener('pointerenter', handlePointerEnter);
    ignoreEl.addEventListener('pointerleave', handlePointerLeave);
  });

  visibilityObserver.observe(hostEl);

  const textureImage = new Image();
  textureImage.decoding = 'async';

  textureImage.onload = () => {
    if (instance.destroyed) return;

    instance.imageWidth = textureImage.naturalWidth || 1;
    instance.imageHeight = textureImage.naturalHeight || 1;

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, textureImage);

    instance.textureReady = true;
    instance.needsRender = true;
    hostEl.classList.add('is-webgl-hover-ready');

    ensureWebglHoverRenderLoop();
  };

  textureImage.onerror = () => {
    destroy();
  };

  textureImage.src = imageSource;

  return true;
};

const initImageWebglHoverEffects = () => {
  if (!isWebglHoverEnabled()) {
    destroyAllWebglHoverEffects();
    return 0;
  }

  let initializedCount = 0;
  const hosts = document.querySelectorAll(WEBGL_HOVER_SELECTOR);

  hosts.forEach((hostEl) => {
    if (webglHoverInstances.has(hostEl)) return;

    if (initWebglHover(hostEl)) {
      initializedCount += 1;
    }
  });

  return initializedCount;
};

const queueSyncImageWebglHoverEffects = () => {
  if (resizeFrameId !== null) return;

  resizeFrameId = window.requestAnimationFrame(() => {
    resizeFrameId = null;
    initImageWebglHoverEffects();
  });
};

window.initImageWebglHoverEffects = initImageWebglHoverEffects;
window.refreshImageWebglHoverEffects = initImageWebglHoverEffects;

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    stopWebglHoverRenderLoop();
    return;
  }

  ensureWebglHoverRenderLoop();
});

window.addEventListener('resize', queueSyncImageWebglHoverEffects, { passive: true });

if (typeof reducedMotionMedia.addEventListener === 'function') {
  reducedMotionMedia.addEventListener('change', queueSyncImageWebglHoverEffects);
} else if (typeof reducedMotionMedia.addListener === 'function') {
  reducedMotionMedia.addListener(queueSyncImageWebglHoverEffects);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageWebglHoverEffects, { once: true });
} else {
  initImageWebglHoverEffects();
}













